import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { TypeOptions } from 'react-toastify';
import TestTabs from '@/components/TestTabs.tsx';
import Separator from '@/components/Separator.tsx';
import { CollectionItem, CollectionItemProps } from '@/components/CollectionsList.tsx';
import { TypoBody, TypoHeading, TypoHeading2 } from '@/components/Typography.tsx';
import notify, { dismissAllNotifications } from '@/utils/notifications.tsx';
import MissingTests from '@/components/MissingTests.tsx';
import EXAM_GROUPS_QUERY from '@/graphql/queries/exam-groups.graphql';
import ORDERS_QUERY from '@/graphql/queries/user-orders.graphql';

const Dashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = Number(searchParams.get('order_id'));
  const { data } = useQuery(ORDERS_QUERY, { skip: !orderId });
  const orders = useMemo(() => data?.user?.orders ?? [], [data]);
  const currentOrder = orders.find((o: { orderId: number }) => o.orderId === orderId);

  useEffect(() => {
    let timeoutId: any;

    if (currentOrder) {
      const { product } = currentOrder?.productPrice ?? {};
      const { type, message } = getOrderStatus(currentOrder.status);

      notify(product.name, message, { type });
      timeoutId = setTimeout(() => setSearchParams(''), 3000);
    }

    return () => {
      if (currentOrder) dismissAllNotifications();
      clearTimeout(timeoutId);
    };
  }, [currentOrder, orderId, setSearchParams]);

  return (
    <>
      <TypoHeading2 className="text-neutral-text-strong font-medium px-10 pt-6 pb-4
                               m:px-4 m:pt-2 m:pb-3 m:text-heading2-m">
        Dashboard
      </TypoHeading2>
      <TestSection />
    </>
  );
};

const TestSection = () => {
  const { list, loading } = useExamGroups();

  const { packs, tests, modules, ungrouped } = useMemo(() => {
    const packs = list.filter(i => i.section === GroupSection.PACKS);
    const tests = list.filter(i => i.section === GroupSection.TESTS);
    const modules = list.filter(i => i.section === GroupSection.MODULES);
    const ungrouped = list.filter(i => !i.section);
    return { packs, tests, modules, ungrouped };
  }, [list]);


  if (loading) return null;

  return (
    <div className="w-full bg-neutral2 text-neutral2-text-soft rounded px-10 pt-4 pb-8
                    m:px-4 m:pt-3 m:pb-5">
      <TypoHeading className="text-neutral-text font-medium py-2">
        Practice Makes Perfect
      </TypoHeading>
      <TypoBody className="mb-8">
        Take one of your full-length practice tests or work through one of your SATPrac packs
        and take a step towards perfection!
      </TypoBody>

      {!loading && list.length === 0 && <MissingTests text="No tests available." />}
      {!loading && (
        <>
          {
            Boolean(tests?.length) &&
            <div>
              <TypoBody className="text-neutral-text font-medium py-2">SATPrac Practice Tests</TypoBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
                {
                  tests.map((item) =>
                    <CollectionItem key={item.id} className="w-full" {...item} />
                  )
                }
              </div>
            </div>
          }
          {
            Boolean(packs?.length) &&
            <div className="mt-8">
              <TypoBody className="text-neutral-text font-medium py-2">SATPrac Practice Packs</TypoBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
                {
                  packs.map((item) =>
                    <CollectionItem key={item.id} className="w-full" {...item} />
                  )
                }
              </div>
            </div>
          }
          {
            Boolean(modules?.length) &&
            <div className="mt-8">
              <TypoBody className="text-neutral-text font-medium py-2">SATPrac Modules</TypoBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
                {
                  modules.map((item) =>
                    <CollectionItem key={item.id} className="w-full" {...item} icon="MODULE" />
                  )
                }
              </div>
            </div>
          }
          {
            Boolean(ungrouped?.length) &&
            <div className="mt-8">
              <TypoBody className="text-neutral-text font-medium py-2">Uncategorized</TypoBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-6">
                {
                  ungrouped.map((item) =>
                    <CollectionItem key={item.id} className="w-full" {...item} />
                  )
                }
              </div>
            </div>
          }
        </>
      )}
      <Separator className="my-14" />
      <TestTabs />
    </div>
  );
};

const useExamGroups = () => {
  const { data, loading } = useQuery(EXAM_GROUPS_QUERY);

  const list: (CollectionItemProps & { section: string })[] = useMemo(() => (data?.examGroups ?? [])
    .map((group: any) => {
      const icon = group.subtypes[0]?.icon;

      return {
        id: group.examGroupId,
        name: group.name,
        type: 'collection',
        icon,
        to: `/practice-packs/${group.examGroupId}`,
        section: group.section,
      };
    }), [data?.examGroups]);

  return { loading, list };
};

enum GroupSection {
  TESTS = 'tests',
  PACKS = 'packs',
  MODULES = 'modules',
}

enum OrderStatus {
  FAILED = 'FAILED',
  PAID = 'PAID',
  PENDING = 'PENDING',
  REFUNDED = 'REFUNDED'
}

const getOrderStatus = (status: OrderStatus) => {
  let type: TypeOptions;
  let message: string;

  switch (status) {
  case OrderStatus.PAID:
    type = 'success';
    message = 'Product purchased successfully!';
    break;
  case OrderStatus.FAILED:
    type = 'error';
    message = 'Order failed!';
    break;
  case OrderStatus.PENDING:
    type = 'warning';
    message = 'Order is pending!';
    break;
  case OrderStatus.REFUNDED:
    type = 'info';
    message = 'Order refunded!';
    break;

  default:
    type = 'default';
    message = `Order status: ${status}`;
    break;
  }

  return { type, message };
};

export default Dashboard;
